<script setup lang="ts">
/**
 * Aura AGM — Notice for Controller (Change in Particulars, CORPORATE) · CA 1967 s.386AH(1).
 * 개인용(NoticeControllerDoc)과 동일 골격, particulars 가 법인(entity name/UEN/legal form/jurisdiction 등).
 * Yes/No 조건부: is_still_controller, has_change (플래그를 fill 에 기록).
 */
import { inject, onMounted, ref, watch } from 'vue';
import CddVarField from '../cdd/CddVarField.vue';
import AgmSigners from '../cdd/AgmSigners.vue';
import { useAgmFill, DOC_SCOPE } from '../cdd/useCddFill';
import './agmDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const FLAGS = ['is_still_controller', 'has_change'] as const;
const DEF: Record<string, boolean> = { is_still_controller: true, has_change: false };
const flags = ref<Record<string, boolean>>({ ...DEF });

function read(k: string): boolean {
  const v = String(fill.values[K(k)] ?? '').trim().toLowerCase();
  if (!v) return DEF[k];
  return v === 'yes' || v === 'true' || v === '1';
}
function sync() { for (const k of FLAGS) flags.value[k] = read(k); }
function toggle(k: string) { flags.value[k] = !flags.value[k]; fill.setValue(K(k), flags.value[k] ? 'yes' : 'no'); }
onMounted(() => {
  for (const k of FLAGS) if (!String(fill.values[K(k)] ?? '').trim()) fill.setValue(K(k), DEF[k] ? 'yes' : 'no');
  sync();
});
watch(() => FLAGS.map((k) => fill.values[K(k)]).join('|'), () => sync());

const PARTICULARS = [
  ['controller_entity_name', 'Entity name'],
  ['controller_entity_uen', 'Unique entity number (UEN)'],
  ['controller_registered_office_address', 'Registered office address'],
  ['controller_entity_email', 'Email address'],
  ['controller_entity_contact_no', 'Contact no.'],
  ['controller_legal_form', 'Legal form'],
  ['controller_jurisdiction', 'Jurisdiction / statute of formation'],
  ['controller_corporate_register_name', 'Corporate entity register name'],
  ['controller_date_of_becoming', 'Date of becoming a controller'],
  ['controller_date_of_cessation', 'Date of cessation as a controller'],
] as const;
</script>

<template>
  <div class="agm-doc">
    <div class="doc-title">FORM OF NOTICE MENTIONED IN SECTION 386AH(1) OF COMPANIES ACT — NOTICE FOR CHANGE IN PARTICULARS</div>
    <p class="doc-p">Date of notice: <CddVarField field-key="notice_date" label="Notice Date" /></p>
    <p class="doc-p">Dear <CddVarField field-key="corporate_controller_name" label="Corporate Controller Name" />,</p>
    <p class="doc-p">
      We know or have reasonable grounds to believe that a relevant change has occurred in your particulars that are stated in the
      register of controllers of <CddVarField field-key="company_name" label="Company Name" />. This notice is sent under section
      386AH(1) of the Companies Act 1967 and requires you to provide the following information within 30 days.
    </p>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">1. Are you still a registrable controller of <CddVarField field-key="company_name" label="Company Name" />?</td>
          <td>
            <span class="doc-toggle" :class="{ off: !flags.is_still_controller }" @click="toggle('is_still_controller')">
              {{ flags.is_still_controller ? '☒ Yes' : '☐ Yes' }} / {{ flags.is_still_controller ? '☐ No' : '☒ No' }}
            </span>
            <div v-if="!flags.is_still_controller" style="margin-top:6px">
              Date ceased: <CddVarField field-key="date_ceased_as_controller" label="Date Ceased" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p">2. Your particulars stated in the register of controllers are set out below.</p>
    <table class="cdd-tbl">
      <tbody>
        <tr v-for="p in PARTICULARS" :key="p[0]">
          <td class="cdd-lcell">{{ p[1] }}</td>
          <td><CddVarField :field-key="p[0]" :label="p[1]" /></td>
        </tr>
      </tbody>
    </table>

    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Has a relevant change in your particulars occurred?</td>
          <td>
            <span class="doc-toggle" :class="{ off: !flags.has_change }" @click="toggle('has_change')">
              {{ flags.has_change ? '☒ Yes' : '☐ Yes' }} / {{ flags.has_change ? '☐ No' : '☒ No' }}
            </span>
            <div v-if="flags.has_change" style="margin-top:6px">
              (a) Date of the change: <CddVarField field-key="date_of_change" label="Date of Change" /><br />
              Particulars of the change: <CddVarField field-key="particulars_of_change" label="Particulars of Change" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:14px">Yours sincerely,</p>
    <div class="sign-block"><div class="sign-line"><CddVarField field-key="secretary_name" label="Company Secretary" /><br />Company Secretary</div></div>

    <div class="sec-title" style="margin-top:16px">ACKNOWLEDGEMENT</div>
    <p class="doc-p">
      I, <CddVarField field-key="controller_representative_name" label="Authorised Representative" />, Identity No.:
      <CddVarField field-key="controller_representative_identification_no" label="Identity No." />, being the Authorised
      Representative of <CddVarField field-key="corporate_controller_name" label="Corporate Controller Name" />, hereby declare
      that the above information provided is true and correct.
    </p>
    <p class="doc-p">Signature:</p>
    <AgmSigners var-key="notice_Signers" board-label="Authorised Representative" />
    <p class="doc-p">Date: <CddVarField field-key="dated_on" label="Dated On" /></p>
  </div>
</template>
